import {makeAutoObservable} from "mobx";
import React from "react";
import {Api, UserProfileResponse} from "../api/api.gen";
import {createApi} from "../api/api";
import {ErrorUtils} from "../utils/ErrorUtils";
import {toast} from "react-toastify";
import {SocketApi} from "proto_socket_typescript";
import {proto} from "../proto/messages";

export class AuthStore {
  static REFRESH_BEFORE_EXPIRY_S = 60 * 5;
  static REFRESH_CHECK_INTERVAL_MS = 60 * 1000;
  private accessToken?: string;
  private refreshToken?: string;
  private refreshInterval?: number;
  api: Api<undefined>;
  socketApi: SocketApi;
  user?: UserProfileResponse;

  get authenticated() {
    return !!this.accessToken;
  }

  hasGroup(groupName: string): boolean {
    return this.user?.groups?.includes(groupName) ?? false;
  }

  get isLessonEditor(): boolean {
    return this.hasGroup('lesson_editor');
  }

  constructor() {
    makeAutoObservable(this);
    this.api = createApi(this);
    this.socketApi = new SocketApi(window.location.port === '3000' ? 'http://localhost:8000/ws/' : '/ws/');
    this.socketApi.getMessageHandler(new proto.RxTokenInvalid()).subscribe(() => this.onTokenInvalid());

    // load auth
    const access = localStorage.getItem("access") ?? undefined;
    const refresh = localStorage.getItem("refresh") ?? undefined;
    if (access && refresh) {
      this.setAuth({access, refresh});
    } else {
      this.setAuth(undefined);
    }
    this.refreshInterval = window.setInterval(() => this.refresh(), AuthStore.REFRESH_CHECK_INTERVAL_MS);
  }

  logout() {
    this.setAuth(undefined);
  }

  setAuth(auth: { access: string, refresh: string } | undefined) {
    const changed = auth?.access !== this.accessToken;
    this.accessToken = auth?.access;
    this.refreshToken = auth?.refresh;
    // an expired token must never reach the socket - the server would reject it and run
    // the queued messages unauthenticated (unauthorized toasts on every initial fetch)
    this.socketApi.setAuth(this.accessToken && !this.accessTokenExpired ? this.accessToken : null);
    if (auth) {
      localStorage.setItem("access", auth.access);
      localStorage.setItem("refresh", auth.refresh);
    } else {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
    }
    if (auth && this.accessTokenExpired) {
      this.refresh();
    }
    if (auth && changed) {
      this.loadUser();
    }
  }

  private get accessTokenExpiresInS(): number | undefined {
    if (!this.accessToken?.length) return undefined;
    try {
      const decoded = JSON.parse(
        atob(this.accessToken
          .split('.')[1]
          .replace(/-/g, "+").replace(/_/g, "/")
        )
      );
      return decoded.exp - Date.now() / 1000;
    } catch (e) {
      return undefined;
    }
  }

  private get accessTokenExpired(): boolean {
    const expiresInS = this.accessTokenExpiresInS;
    return expiresInS !== undefined && expiresInS <= 0;
  }

  private refreshing?: Promise<void>;

  refresh(force = false): Promise<void> | undefined {
    if (this.refreshing) return this.refreshing;
    if (!this.accessToken?.length || !this.refreshToken?.length) return;
    const expiresInS = this.accessTokenExpiresInS;
    if (!force && expiresInS !== undefined && expiresInS > AuthStore.REFRESH_BEFORE_EXPIRY_S) return;
    this.refreshing = (async () => {
      try {
        const response = await this.api.token.tokenRefresh(
          {refresh: this.refreshToken!},
          {secure: false},
        );
        if (response.data.access) {
          this.setAuth({access: response.data.access, refresh: response.data.refresh});
        } else {
          this.setAuth(undefined);
          toast.error('Nepričakovana napaka pri obnovi avtentikacije (access token is null).');
        }
      } catch (e) {
        this.setAuth(undefined);
        ErrorUtils.handleApiError('Napaka pri obnovi avtentikacije', e);
      } finally {
        this.refreshing = undefined;
      }
    })();
    return this.refreshing;
  }

  private onTokenInvalid() {
    // the server rejected our token on the socket - gate socket sends until a fresh one is set
    this.socketApi.setAuth(null);
    this.refresh(true);
  }

  getAuthHeaders(): { [key: string]: string } {
    if (this.authenticated) {
      return {'Authorization': `Bearer ${this.accessToken}`};
    } else {
      return {};
    }
  }

  dispose() {
    window.clearInterval(this.refreshInterval);
  }

  async loadUser() {
    try {
      this.user = (await this.api.auth.authGetProfile()).data;
    } catch (e) {
      ErrorUtils.handleApiError('Napaka pri nalaganju uporabnika', e);
    }
  }
}

export const AuthContext = React.createContext<AuthStore | undefined>(undefined);